import { useCallback, useRef, useState } from 'react'

export type StageId =
  | 'analyze'
  | 'ideas'
  | 'save'
  | 'draw'
  | 'encode'
  | 'persist'
  | 'done'

export type StageStatus = 'wait' | 'active' | 'done' | 'fail'

export interface Stage {
  id: StageId
  label: string
  status: StageStatus
  detail?: string
}

export const INITIAL_STAGES: Stage[] = [
  { id: 'analyze', label: '正在分析游戏构想', status: 'wait' },
  { id: 'ideas', label: '正在生成 3 个不同游戏创意', status: 'wait' },
  { id: 'save', label: '正在保存创意方案', status: 'wait' },
  { id: 'draw', label: '正在绘制像素风概念图', status: 'wait' },
  { id: 'encode', label: '正在转换 JPEG 90%', status: 'wait' },
  { id: 'persist', label: '正在保存图片与历史', status: 'wait' },
  { id: 'done', label: '全部完成', status: 'wait' },
]

export function useGenerationProgress() {
  const [stages, setStages] = useState<Stage[]>(INITIAL_STAGES)
  const [elapsedMs, setElapsedMs] = useState(0)
  const timerRef = useRef<number | null>(null)
  const startRef = useRef(0)

  const setStage = useCallback((id: StageId, status: StageStatus, detail?: string) => {
    setStages((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status, detail: detail ?? s.detail } : s)),
    )
  }, [])

  const markFrom = useCallback((id: StageId) => {
    setStages((prev) => {
      const i = prev.findIndex((s) => s.id === id)
      return prev.map((s, idx) => {
        if (idx < i) return { ...s, status: 'done' as const }
        if (idx === i) return { ...s, status: 'active' as const }
        return { ...s, status: 'wait' as const }
      })
    })
  }, [])

  const startTimer = useCallback(() => {
    startRef.current = Date.now()
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setElapsedMs(Date.now() - startRef.current)
    }, 500)
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  const reset = useCallback(() => {
    setStages(INITIAL_STAGES)
    setElapsedMs(0)
    stopTimer()
  }, [stopTimer])

  return { stages, elapsedMs, setStage, markFrom, startTimer, stopTimer, reset }
}

export function StageList({ stages, elapsedMs }: { stages: Stage[]; elapsedMs: number }) {
  return (
    <div className="stage-list" role="status" aria-live="polite">
      <div className="stage-list-head">
        <span className="refine-title">生成进度</span>
        <span className="muted">已用 {(elapsedMs / 1000).toFixed(1)}s（真实耗时，不含预估）</span>
      </div>
      <ol>
        {stages.map((s) => (
          <li key={s.id} className={`stage stage-${s.status}`}>
            <span className="stage-icon" aria-hidden>
              {s.status === 'done' ? '✓' : s.status === 'active' ? '…' : s.status === 'fail' ? '!' : '·'}
            </span>
            <span className="stage-label">{s.label}</span>
            {s.detail ? <span className="stage-detail">{s.detail}</span> : null}
            {s.status === 'active' ? <span className="spinner stage-spin" aria-hidden /> : null}
          </li>
        ))}
      </ol>
    </div>
  )
}
